/**
 * Citations for Home (docs/HOME.md). Every interval, temperature, dilution and product in src/home cites a row of a
 * research note in docs/source/research/ as "<note> <row id>", e.g. "home-cleaning-appliances K7". A test reads the
 * notes and checks that every cited row exists.
 *
 * Where a source is silent the app says so: `Suggested` carries the one-line reason shown to people, and is never
 * presented as a rule.
 */
import { SOURCE_NAMES } from "./source-names.ts";

export const RESEARCH_NOTES = ["home-food-safety", "home-cleaning-appliances", "home-laundry-bedroom", "home-storage-organization", "hygiene-and-kits", "thaw-and-storage", "dishwasher-and-dishes", "grooming-and-guests", "home-maintenance-bathroom", "upholstery-seating"] as const;
export type ResearchNote = (typeof RESEARCH_NOTES)[number];

/** "home-food-safety H27" */
export type Citation = string;

export const food = (...rows: string[]): Citation[] => rows.map((r) => `home-food-safety ${r}`);
export const clean = (...rows: string[]): Citation[] => rows.map((r) => `home-cleaning-appliances ${r}`);
export const laundry = (...rows: string[]): Citation[] => rows.map((r) => `home-laundry-bedroom ${r}`);
export const storage = (...rows: string[]): Citation[] => rows.map((r) => `home-storage-organization ${r}`);
export const hygiene = (...rows: string[]): Citation[] => rows.map((r) => `hygiene-and-kits ${r}`);
export const thaw = (...rows: string[]): Citation[] => rows.map((r) => `thaw-and-storage ${r}`);
export const groom = (...rows: string[]): Citation[] => rows.map((r) => `grooming-and-guests ${r}`);
export const bathroom = (...rows: string[]): Citation[] => rows.map((r) => `home-maintenance-bathroom ${r}`);
export const upholstery = (...rows: string[]): Citation[] => rows.map((r) => `upholstery-seating ${r}`);
export const dishes = (...rows: string[]): Citation[] => rows.map((r) => `dishwasher-and-dishes ${r}`);

/** Parses "home-food-safety H27" → { note, row }. Undefined when it isn't a research citation. */
export function parseCitation(c: string): { note: ResearchNote; row: string } | undefined {
  const m = c.match(/^([a-z-]+) ([A-Z]+\d+|Kit \d)$/);
  if (!m || !(RESEARCH_NOTES as readonly string[]).includes(m[1]!)) return undefined;
  return { note: m[1] as ResearchNote, row: m[2]! };
}

/**
 * A citation as people read it: the publishers its research row names ("home-food-safety H48" → "Clemson Extension")
 * and the row's URL; the code stays for reviewers. A citation we can't name keeps its code as the name (known: false).
 */
export interface SourceLabel { code: string; name: string; url?: string; known: boolean }

export function sourceLabel(citation: Citation): SourceLabel {
  const code = citation.replace(/^home:/, "");
  const hit = SOURCE_NAMES[code];
  if (!hit) return { code, name: code, known: false };
  return { code, name: hit[0], ...(hit[1] ? { url: hit[1] } : {}), known: true };
}

/** The readable names of a list of citations, each publisher once, in first-cited order: "Clemson Extension, FDA Food Code". */
export function sourceNames(citations: readonly Citation[]): string {
  const names: string[] = [];
  for (const c of citations) for (const n of sourceLabel(c).name.split(", ")) if (!names.includes(n)) names.push(n);
  return names.join(", ");
}

/** No source gives this; shown to people as a starting point, never as a rule. */
export interface Suggested { suggested: true; reason: string }
export const suggest = (reason: string): Suggested => ({ suggested: true, reason });
export const NO_INTERVAL = "No official interval; this is a common starting point.";

/** Shown with every product list. */
export const NOT_ENDORSED = "Examples, not endorsements.";
